import React, { useState, useEffect, createContext, useContext } from 'react';
import i18n from '../i18n';
import './NotificationSystem.css';

// Notification Context
const NotificationContext = createContext();

// Custom hook to use notifications
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

// Notification Provider Component
export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const addNotification = (notification) => {
    const id = Date.now() + Math.random();
    const newNotification = {
      id,
      ...notification,
      timestamp: new Date(),
      autoClose: notification.autoClose !== false, // Default to true
      duration: notification.duration || 5000 // Default 5 seconds
    };

    setNotifications(prev => [...prev, newNotification]);

    // Auto remove after duration
    if (newNotification.autoClose) {
      setTimeout(() => {
        removeNotification(id);
      }, newNotification.duration);
    }

    return id;
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };

  const clearAllNotifications = () => {
    setNotifications([]);
  };

  // Convenience methods for common notification types (default titles translated)
  const notify = {
    success: (message, title) => addNotification({
      type: 'success',
      title: title ?? i18n.t('common:success'),
      message,
      icon: '✅'
    }),

    error: (message, title) => addNotification({
      type: 'error',
      title: title ?? i18n.t('common:error'),
      message,
      icon: '❌',
      autoClose: false // Errors don't auto-close
    }),

    warning: (message, title) => addNotification({
      type: 'warning',
      title: title ?? i18n.t('common:warning'),
      message,
      icon: '⚠️'
    }),

    info: (message, title) => addNotification({
      type: 'info',
      title: title ?? i18n.t('common:information'),
      message,
      icon: 'ℹ️'
    }),

    userCreated: (userName, userEmail) => addNotification({
      type: 'success',
      title: 'User Created Successfully!',
      message: `${userName} can now log in with their email: ${userEmail}`,
      icon: '👤',
      duration: 7000,
      actions: [
        {
          label: 'Send Login Instructions',
          action: () => {
            // TODO: Implement send login instructions
            notify.info('Login instructions will be sent via email');
          }
        }
      ]
    }),

    biologicalImported: (count) => addNotification({
      type: 'success',
      title: 'Biological Entities Imported!',
      message: `Successfully imported ${count} biological entities`,
      icon: '🦠',
      duration: 6000
    }),

    dataSaved: (entityType) => addNotification({
      type: 'success',
      title: 'Data Saved',
      message: `${entityType} saved successfully`,
      icon: '💾',
      duration: 3000
    }),

    permissionDenied: (action) => addNotification({
      type: 'error',
      title: 'Access Denied',
      message: `You don't have permission to ${action}`,
      icon: '🚫',
      autoClose: false
    })
  };

  return (
    <NotificationContext.Provider value={{
      notifications,
      addNotification,
      removeNotification,
      clearAllNotifications,
      notify
    }}>
      {children}
      <NotificationContainer notifications={notifications} onRemove={removeNotification} />
    </NotificationContext.Provider>
  );
};

// Notification Container Component
const NotificationContainer = ({ notifications, onRemove }) => {
  return (
    <div className="notification-container">
      {notifications.map(notification => (
        <NotificationItem
          key={notification.id}
          notification={notification}
          onRemove={onRemove}
        />
      ))}
    </div>
  );
};

// Individual Notification Component
const NotificationItem = ({ notification, onRemove }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    // Trigger entrance animation
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  const handleRemove = () => {
    setIsLeaving(true);
    setTimeout(() => {
      onRemove(notification.id);
    }, 300); // Match CSS transition duration
  };

  const handleAction = (action) => {
    if (action.action) {
      action.action();
    }
    handleRemove();
  };

  return (
    <div className={`notification-item ${notification.type} ${isVisible ? 'visible' : ''} ${isLeaving ? 'leaving' : ''}`}>
      <div className="notification-content">
        <div className="notification-icon">
          {notification.icon}
        </div>
        
        <div className="notification-text">
          <div className="notification-title">
            {notification.title}
          </div>
          <div className="notification-message">
            {notification.message}
          </div>
          
          {notification.actions && notification.actions.length > 0 && (
            <div className="notification-actions">
              {notification.actions.map((action, index) => (
                <button
                  key={index}
                  className="notification-action-btn"
                  onClick={() => handleAction(action)}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>
        
        <button 
          className="notification-close"
          onClick={handleRemove}
          aria-label="Close notification"
        >
          ✕
        </button>
      </div>
      
      {notification.autoClose && (
        <div className="notification-progress">
          <div 
            className="notification-progress-bar"
            style={{
              animationDuration: `${notification.duration}ms`
            }}
          />
        </div>
      )}
    </div>
  );
};
